// WHAT: Oura Sync Hook
// WHY:  Provides sync functionality with error handling and navigation
// DOMAIN: Health → Oura
// DEPENDENCIES: React Router, Oura sync service, debug logging
// RISKS:
//   - NOTE: Navigates to setup page on token errors (may interrupt user flow)
//   - NOTE: NoDataAvailable status triggers onNoData callback (behavior depends on caller)
//   - NOTE: Error handling uses string matching for token errors (fragile)

import type { BackendSyncStatus } from "@/domains/health/oura/data/ouraTypes";

import { useNavigate } from "react-router-dom";

import { syncOuraDataFromAPI } from "@/domains/health/oura/services";
import { debugLog } from "@/domains/logging/utils/debugLog";

type SyncFunction = () => Promise<BackendSyncStatus>;
const safeSyncOuraDataFromAPI: SyncFunction = syncOuraDataFromAPI as SyncFunction;

/**
 * WHAT:
 *   Custom hook providing Oura data synchronization with error handling.
 *
 * WHY:
 *   Encapsulates sync logic with navigation and reload functionality.
 *   Handles token errors by redirecting to setup page.
 *
 * INPUTS:
 *   - reload: () => void — Callback to reload Oura data after successful sync
 *
 * @returns handleSync function for syncing Oura data
 *
 * BEHAVIOR:
 *   - Calls syncOuraDataFromAPI service function
 *   - Handles NoDataAvailable status by calling onNoData callback
 *   - Reloads data after successful sync
 *   - Detects token errors by string matching ("Token" in error message)
 *   - Navigates to setup page on token errors
 *   - Logs all operations using debug logging
 *
 * RISKS:
 *   - NOTE: Token error detection is fragile (string matching)
 *   - NOTE: Navigation to setup interrupts user flow (no confirmation)
 *   - NOTE: onNoData callback behavior depends on caller implementation
 *
 * @param reload
 *
 */
export const useOuraSync = (reload: () => void) => {
  const navigate = useNavigate();

  const handleSync = async (onNoData: () => void) => {
    debugLog("info", "health", "Starting Oura sync from frontend", {
      file: "useOuraSync.ts",
      function: "handleSync",
      module: "oura",
    });

    try {
      const result = await safeSyncOuraDataFromAPI();
      debugLog("info", "health", "Oura sync completed successfully", {
        file: "useOuraSync.ts",
        function: "handleSync",
        module: "oura",
      });

      if (result.status === "NoDataAvailable") {
        debugLog("warn", "health", "No data available from Oura API", {
          file: "useOuraSync.ts",
          function: "handleSync",
          module: "oura",
        });
        onNoData();
        return;
      }

      debugLog("info", "health", "Reloading Oura data after successful sync", {
        file: "useOuraSync.ts",
        function: "handleSync",
        module: "oura",
      });
      reload();
    } catch (error: unknown) {
      let message: string;
      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === "string") {
        message = error;
      } else if (
        error !== null &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message: unknown }).message === "string"
      ) {
        message = (error as { message: string }).message;
      } else {
        message = String(error);
      }
      debugLog("error", "health", `Oura sync failed: ${message}`, {
        file: "useOuraSync.ts",
        function: "handleSync",
        module: "oura",
      });

      if (typeof message === "string" && message.includes("Token")) {
        debugLog("warn", "health", "Token error detected, redirecting to setup", {
          file: "useOuraSync.ts",
          function: "handleSync",
          module: "oura",
        });

        navigate("/health/oura/setup");
      }
    }
  };

  return handleSync;
};
